import React, { FC } from 'react';
import { Modal, Button, Divider } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import styles from './RevocationWarningModal.module.scss';

const nameSpace = 'filesTable.revocationWarningModal';

interface RevocationWarningModalProps {
    isOpen: boolean;
    userEmail: string;
    isGroup?: boolean;
    isForwardingEnabled: boolean;
    isDomainForwardingEnabled: boolean;
    onContinue: () => void;
    onChangePolicy: () => void;
    onRevokeAllDomain?: () => void;
    onCancel: () => void;
}

const RevocationWarningModal: FC<RevocationWarningModalProps> = ({
    isOpen,
    userEmail,
    isGroup = false,
    isForwardingEnabled,
    isDomainForwardingEnabled,
    onContinue,
    onChangePolicy,
    onRevokeAllDomain,
    onCancel,
}) => {
    const { t } = useTranslation();
    const userDomain = userEmail.split('@')[1] || '';

    const renderWarningMessage = (): JSX.Element => {
        if (isForwardingEnabled || (isDomainForwardingEnabled && isGroup)) {
            return (
                <p className={styles['warning-message']}>
                    {t(`${nameSpace}.forwardingWarningPart1`)}{' '}
                    <span className={styles['highlight']}>{userEmail}</span>{' '}
                    {t(`${nameSpace}.forwardingWarningPart2`)}
                </p>
            );
        }
        if (isDomainForwardingEnabled) {
            return (
                <p className={styles['warning-message']}>
                    {t(`${nameSpace}.domainForwardingWarningPart1`)}{' '}
                    <span className={styles['highlight']}>{userEmail}</span>{' '}
                    {t(`${nameSpace}.domainRevokeWarning`, { domain: userDomain })}
                </p>
            );
        }
        return null;
    };

    return (
        <Modal
            open={isOpen}
            onCancel={onCancel}
            footer={null}
            centered
            width={520}
            className={styles['revocation-warning-modal']}
        >
            <div className={styles['modal-content']}>
                <div className={styles['warning-header']}>
                    <WarningOutlined className={styles['warning-icon']} />
                    <span className={styles['title']}>
                        {isDomainForwardingEnabled && !isForwardingEnabled && !isGroup
                            ? t(`${nameSpace}.domainForwardingEnabledTitle`)
                            : t(`${nameSpace}.forwardingEnabledTitle`)}
                    </span>
                </div>
                
                <Divider />
                {renderWarningMessage()}
                <Divider />

                <div className={styles['action-buttons']}>
                    <Button 
                        className={styles['action-button']}
                        onClick={onContinue}
                    >
                        {t(`${nameSpace}.continueRevocation`)}
                    </Button>
                    
                    <Button 
                        className={styles['action-button']}
                        onClick={onChangePolicy}
                    >
                        {t(`${nameSpace}.changeSharingPolicy`)}
                    </Button>
                    
                    {isDomainForwardingEnabled && !isForwardingEnabled && !isGroup && onRevokeAllDomain && (
                        <Button 
                            className={`${styles['action-button']} ${styles['danger-button']}`}
                            onClick={onRevokeAllDomain}
                        >
                            <span>
                            {t(`${nameSpace}.revokeAllDomainAccess`)}{' '}
                            <span className={styles['highlight']}>@{userDomain}</span>{' '}
                            {t(`${nameSpace}.revokeAllDomainAccessSuffix`)}
                            </span>
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default RevocationWarningModal;
